import { type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import type { Mails } from 'modules/Mails/hooks/use-load-mails';
import { MAIL_CATEGORIES } from 'modules/Mails/types/mail-category';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const updateMailVariablesSchema = z.object({
  id: z.string(),
  categories: z.array(z.enum(MAIL_CATEGORIES)),
});

export type UpdateMailVariables = z.infer<typeof updateMailVariablesSchema>;

export async function updateMailFn(
  axiosInstance: Promise<AxiosInstance>,
  data: UpdateMailVariables,
) {
  const instance = await axiosInstance;
  return instance.put(`mails/${data.id}`, data);
}

type UseUpdateMailOptions = {
  disableSuccessNotification?: boolean;
};

export function useUpdateMail({ disableSuccessNotification = false }: UseUpdateMailOptions = {}) {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateMailVariables, OptimisticContext>({
    mutationFn: variables => updateMailFn(axiosInstance, variables),
    onMutate: variables => applyOptimisticUpdate(client, variables),
    onError: (_err, _variables, context) => rollbackOptimisticUpdate(client, context),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['mails'] });
      client.invalidateQueries({ queryKey: ['mail'] });
    },
    meta: disableSuccessNotification ? { suppressSuccessNotification: true } : undefined,
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, UpdateMailVariables, 'updateMail'>(
    'updateMail',
    mutation,
  );
}

type OptimisticContext = { previousQueries: [QueryKey, Mails | undefined][] };

async function applyOptimisticUpdate(
  client: ReturnType<typeof useQueryClient>,
  variables: UpdateMailVariables,
): Promise<OptimisticContext> {
  await client.cancelQueries({ queryKey: ['mails'] });

  const previousQueries = client.getQueriesData<Mails>({ queryKey: ['mails'] });
  const { id, categories } = variables;

  client.setQueriesData<Mails>({ queryKey: ['mails'] }, old => {
    if (!old?.threads) return old;
    return {
      ...old,
      threads: old.threads.map(thread =>
        thread.mailDetail.id === id
          ? { ...thread, mailDetail: { ...thread.mailDetail, categories } }
          : thread,
      ),
    };
  });

  return { previousQueries };
}

function rollbackOptimisticUpdate(
  client: ReturnType<typeof useQueryClient>,
  context: OptimisticContext | undefined,
) {
  context?.previousQueries.forEach(([queryKey, data]) => {
    client.setQueryData(queryKey, data);
  });
}
